/**
 * Retry timing for both outboxes.
 *
 * Two queues use this: the server's notification outbox, and the operator app's
 * offline submission queue. They share the mechanism but not the policy, because
 * they fail differently — an undeliverable webhook should eventually wake a human,
 * while a queued inspection must never be abandoned.
 */

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;

export interface BackoffSchedule {
  /** Delays for successive attempts. The last entry repeats indefinitely. */
  readonly delaysMs: readonly number[];
  /** Attempts after which the queue gives up. Absent means it never does. */
  readonly maxAttempts?: number;
  /** Fraction of the delay to spread retries across, so they do not synchronise. */
  readonly jitterRatio: number;
}

/**
 * Outbound notifications. After eight attempts the row is marked dead and TES is
 * emailed directly — a webhook that has failed for a day is a configuration problem
 * a person needs to see, not something to keep retrying quietly.
 */
export const NOTIFICATION_BACKOFF: BackoffSchedule = {
  delaysMs: [30 * SECOND, 2 * MINUTE, 10 * MINUTE, HOUR, 6 * HOUR, 24 * HOUR],
  maxAttempts: 8,
  jitterRatio: 0.2,
};

/**
 * Inspections and defect reports queued on an operator's phone. There is no
 * `maxAttempts`: a safety record is never discarded, however long the phone spends
 * out of signal. A terminal rejection from the server is handled by the caller, not
 * by running out of retries.
 */
export const SUBMISSION_BACKOFF: BackoffSchedule = {
  delaysMs: [2 * SECOND, 5 * SECOND, 15 * SECOND, MINUTE, 5 * MINUTE, 15 * MINUTE, 30 * MINUTE],
  jitterRatio: 0.2,
};

/**
 * How long to wait before attempt number `attempt`.
 *
 * `randomFraction` is a value in [0, 1] supplied by the caller rather than drawn
 * here, which keeps this module free of a random source and makes the jitter band
 * assertable in a test.
 */
export function nextAttemptDelayMs(
  schedule: BackoffSchedule,
  attempt: number,
  randomFraction: number,
): number {
  return applyJitter(scheduledDelayMs(schedule, attempt), schedule.jitterRatio, randomFraction);
}

export function isExhausted(schedule: BackoffSchedule, attempt: number): boolean {
  return schedule.maxAttempts !== undefined && attempt >= schedule.maxAttempts;
}

function scheduledDelayMs(schedule: BackoffSchedule, attempt: number): number {
  const { delaysMs } = schedule;
  const index = Math.min(Math.max(attempt, 1) - 1, delaysMs.length - 1);

  return delaysMs[index] ?? delaysMs[delaysMs.length - 1] ?? SECOND;
}

function applyJitter(delayMs: number, jitterRatio: number, randomFraction: number): number {
  const offsetFromCentre = randomFraction * 2 - 1;
  return delayMs * (1 + jitterRatio * offsetFromCentre);
}
